import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import HeroCarousel from "@/components/HeroCarousel";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero() {
    const [hover, setHover] = useState(false);

    return (
        <section
            id="inicio"
            className="relative flex min-h-[70svh] flex-col justify-end overflow-hidden md:min-h-screen"
            data-testid="hero-section"
            onMouseEnter={() => window.matchMedia("(hover: hover)").matches && setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <HeroCarousel paused={hover} />

            <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-16 pt-32 md:px-12 md:pb-20 md:pt-40">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.1 }}
                    className="mb-8 text-[11px] uppercase tracking-[0.3em] text-gold"
                    data-testid="hero-overline"
                >
                    Firma contable · Cali, Colombia
                </motion.p>

                <h1
                    className="font-display text-[11.5vw] font-medium leading-[1.02] tracking-tighter sm:text-[9vw] lg:text-[6.6vw]"
                    data-testid="hero-headline"
                >
                    <MaskedLine delay={0.25}>Contabilidad,</MaskedLine>
                    <MaskedLine delay={0.4}>impuestos y</MaskedLine>
                    <MaskedLine delay={0.55} className="italic text-gold">revisoría fiscal</MaskedLine>
                    <MaskedLine delay={0.7}>para PYMES en Colombia.</MaskedLine>
                </h1>

                <div className="mt-12 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
                        className="max-w-md text-base leading-relaxed text-[#9CA3AF] md:text-lg"
                        data-testid="hero-subtitle"
                    >
                        Gerencia tributaria y NIIF para PYMES con rigor técnico y trato
                        directo. Convertimos tus obligaciones fiscales en tranquilidad
                        y ventaja competitiva.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
                        className="flex flex-col gap-4 sm:flex-row"
                    >
                        <button
                            onClick={() => scrollToId("contacto")}
                            className="group flex items-center justify-center gap-3 bg-gold px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-navy transition-colors duration-300 hover:bg-gold-soft"
                            data-testid="hero-cta-button"
                        >
                            Solicitar asesoría
                            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            onClick={() => scrollToId("servicios")}
                            className="flex items-center justify-center gap-3 border border-white/25 px-8 py-4 text-[11px] uppercase tracking-[0.22em] text-[#F8F9FA] transition-colors duration-300 hover:border-gold hover:text-gold"
                            data-testid="hero-services-button"
                        >
                            Conocer servicios
                        </button>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2, delay: 1.4 }}
                    className="mt-16 grid grid-cols-1 gap-6 border-t border-white/10 pt-8 sm:grid-cols-3"
                    data-testid="hero-stats"
                >
                    {[
                        ["+10", "años de experiencia profesional"],
                        ["+25", "empresas acompañadas en Colombia"],
                        ["100%", "compromiso con el cumplimiento DIAN"],
                    ].map(([num, label]) => (
                        <div key={label} className="flex items-baseline gap-4">
                            <span className="font-display text-3xl text-gold md:text-4xl">{num}</span>
                            <span className="max-w-[180px] text-xs uppercase tracking-[0.15em] text-[#9CA3AF]">{label}</span>
                        </div>
                    ))}
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-6 left-6 hidden text-gold/60 md:left-12 md:block"
                aria-hidden="true"
            >
                <ArrowDown size={20} className="animate-bounce" />
            </motion.div>
        </section>
    );
}
